/**
 * @file TenantSettingsPage.tsx
 * @description US1 — Tenant Settings Audit page with KPI cards, filters, table, CSV export
 * @feature 142-governance-compliance-dashboard
 * @constitution v1.19.0
 *
 * Conformed to Spec 197 design blueprint — migrated off the hand-rolled
 * header/KPI/filter/table/state markup onto the canonical Tailwind+token UI kit
 * (PageLayout, Card, DataTable, StatCard, Button, Input, Select, Badge, states).
 */

import React, { useState, useMemo } from 'react';
import { ShieldCheck, Settings, AlertTriangle, Lock, Search, Download, Database } from 'lucide-react';
import { useTenantSettings, downloadCsv } from '../../../services/operations/governanceService';
import type { TenantSetting } from '../../../types/governance';
import {
  PageLayout,
  Card,
  DataTable,
  FilterBar,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  NotCollected,
  AppTooltip,
  Extended,
  QuickGlance,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';

// ============================================================================
// Main Component
// ============================================================================

const NOT_COLLECTED_REASON =
  'Tenant-settings governance data has not been collected yet — the governance collector requires Fabric Admin API access.';

const TenantSettingsPage: React.FC = () => {
  const { data, isLoading, error, refetch } = useTenantSettings();

  // Filters
  const [enabledFilter, setEnabledFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filtered = useMemo(() => {
    if (!data?.settings) return [];
    let list = data.settings;

    if (enabledFilter !== 'all') {
      const enabled = enabledFilter === 'enabled';
      list = list.filter((s) => s.isEnabled === enabled);
    }
    if (categoryFilter !== 'all') {
      list = list.filter((s) => s.category === categoryFilter);
    }
    if (riskFilter !== 'all') {
      list = list.filter((s) => s.riskLevel === riskFilter);
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter(
        (s) =>
          s.settingName.toLowerCase().includes(lower) ||
          (s.settingTitle && s.settingTitle.toLowerCase().includes(lower)),
      );
    }

    return list;
  }, [data?.settings, enabledFilter, categoryFilter, riskFilter, searchTerm]);

  const handleExportCsv = () => {
    const date = new Date().toISOString().split('T')[0];
    let csv = 'Setting Name,Title,Category,Status,Restricted To,Risk Level,Can Restrict,Last Collected\n';
    filtered.forEach((s) => {
      csv += `"${s.settingName}","${s.settingTitle || ''}","${s.category || ''}","${s.isEnabled ? 'Enabled' : 'Disabled'}","${s.restrictionDisplay}","${s.riskLevel}","${s.canSpecifySecurityGroups == null ? '' : s.canSpecifySecurityGroups ? 'Yes' : 'No'}","${s.snapshotDate || ''}"\n`;
    });
    downloadCsv(csv, `tenant-settings-${date}.csv`);
  };

  const isEmpty = !isLoading && (!data?.settings || data.settings.length === 0);
  const summary = data?.summary;

  const columns: DataTableColumn<TenantSetting>[] = [
    {
      key: 'settingName',
      header: 'Setting Name',
      width: '34%',
      render: (s) => (
        <div className="min-w-0">
          <div className="font-medium text-[var(--color-text-primary)]">{s.settingTitle || s.settingName}</div>
          {s.settingTitle && <div className="text-xs text-[var(--color-text-secondary)]">{s.settingName}</div>}
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      width: '14%',
      render: (s) => s.category || <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'isEnabled',
      header: 'Status',
      width: '10%',
      render: (s) => (
        <Badge variant={s.isEnabled ? 'success' : 'secondary'}>{s.isEnabled ? 'Enabled' : 'Disabled'}</Badge>
      ),
    },
    {
      key: 'restrictionDisplay',
      header: 'Restricted To',
      width: '20%',
      render: (s) => (
        <div className="flex items-center gap-1.5">
          <span className="text-[var(--color-text-primary)]">{s.restrictionDisplay}</span>
          {s.riskLevel === 'unrestricted' && (
            <AppTooltip title="Unrestricted — available to all users">
              <AlertTriangle className="h-4 w-4 text-[var(--color-status-warning)]" />
            </AppTooltip>
          )}
        </div>
      ),
    },
    {
      key: 'canSpecifySecurityGroups',
      header: 'Can Restrict',
      width: '10%',
      render: (s) =>
        s.canSpecifySecurityGroups == null ? (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ) : s.canSpecifySecurityGroups ? (
          'Yes'
        ) : (
          'No'
        ),
    },
    {
      key: 'snapshotDate',
      header: 'Last Collected',
      width: '12%',
      render: (s) => (
        <span className="text-[var(--color-text-secondary)]">{s.snapshotDate || '—'}</span>
      ),
    },
  ];

  return (
    <PageLayout
      title="Tenant Settings"
      description="Fabric tenant governance policy overview"
      icon={<ShieldCheck className="h-6 w-6" />}
            helpId="governance.tenant-settings"
            onRefresh={() => { void refetch(); }}
            isRefreshing={isLoading}
            actions={
        <>
          <Button leftIcon={<Download className="h-4 w-4" />} onClick={handleExportCsv} disabled={isEmpty}>
            Export CSV
          </Button>
        </>
      }
    >
      {error ? (
        <ErrorState
          title="Failed to load tenant settings"
          message={error instanceof Error ? error.message : 'Unknown error'}
          onRetry={() => refetch()}
        />
      ) : (
        <>
          {/* KPI tiles — Spec 216 T020: a tile with NO collected data shows the
              explicit not-collected label, never a bare 0/— (FR-007). */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              label="Total Settings"
              value={isEmpty ? <NotCollected reason={NOT_COLLECTED_REASON} /> : summary?.totalSettings ?? '—'}
              icon={<Settings className="h-4 w-4" />}
            />
            <StatCard
              label="Enabled"
              value={isEmpty ? <NotCollected reason={NOT_COLLECTED_REASON} /> : summary?.enabledCount ?? '—'}
              valueClassName="text-[var(--color-status-success)]"
              icon={<ShieldCheck className="h-4 w-4" />}
            />
            <StatCard
              label="Restricted"
              value={isEmpty ? <NotCollected reason={NOT_COLLECTED_REASON} /> : summary?.restrictedCount ?? '—'}
              icon={<Lock className="h-4 w-4" />}
            />
            <StatCard
              label="Unrestricted Risk"
              value={isEmpty ? <NotCollected reason={NOT_COLLECTED_REASON} /> : summary?.riskCount ?? '—'}
              valueClassName={(summary?.riskCount ?? 0) > 0 ? 'text-[var(--color-status-danger)]' : undefined}
              icon={<AlertTriangle className="h-4 w-4" />}
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the full details.
            </Card>
          </QuickGlance>

          {isLoading ? (
            <Card padding="none">
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            </Card>
          ) : isEmpty ? (
            <Card>
              <EmptyState
                icon={<Database className="h-12 w-12" />}
                title="No governance data collected yet"
                message="Governance collection requires Fabric Admin API access. Contact your admin to enable the governance collector."
              />
            </Card>
          ) : (
            <Extended>
              {/* Filters / search */}
              <FilterBar
                trailing={
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    Showing {filtered.length} of {data?.settings?.length ?? 0}
                  </span>
                }
              >
                <div className="min-w-[16rem] flex-1">
                  <Input
                    isFullWidth
                    placeholder="Search settings..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    leftAddon={<Search className="h-4 w-4" />}
                  />
                </div>
                <Select
                  label="Status"
                  value={enabledFilter}
                  onChange={(e) => setEnabledFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'enabled', label: 'Enabled' },
                    { value: 'disabled', label: 'Disabled' },
                  ]}
                />
                <Select
                  label="Category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'All Categories' },
                    ...(data?.categories?.map((cat) => ({ value: cat, label: cat })) ?? []),
                  ]}
                />
                <Select
                  label="Risk"
                  value={riskFilter}
                  onChange={(e) => setRiskFilter(e.target.value)}
                  options={[
                    { value: 'all', label: 'All Risk' },
                    { value: 'unrestricted', label: 'Unrestricted' },
                    { value: 'restricted', label: 'Restricted' },
                    { value: 'disabled', label: 'Disabled' },
                  ]}
                />
              </FilterBar>

              {/* Table */}
              <Card padding="none">
                <DataTable
                  columns={columns}
                  rows={filtered}
                  rowKey={(s, idx) => `${s.settingName}-${idx}`}
                  pageSize={10}
                  emptyMessage="No settings match the current filters"
                  minWidth="52rem"
                />
              </Card>
            </Extended>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default TenantSettingsPage;
